import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { DropdownShell, IconTile } from "./NavPrimitives";
import { CATEGORY_ICONS } from "./navIcons";

const resolve = (group, cat) =>
  cat.slugs.map((slug) => group.services.find((x) => x.slug === slug)).filter(Boolean);

/** One category column: category header + its services with line icons. */
function GroupColumn({ group, cat, onNavigate }) {
  const CatIcon = CATEGORY_ICONS[cat.title];
  return (
    <div className="bg-white p-4">
      <div className="flex items-center gap-2 pb-2 mb-2 border-b border-slate-100">
        {CatIcon && <CatIcon className="h-3.5 w-3.5 text-orange-survey" />}
        <span className="mono text-[10px] tracking-[0.2em] uppercase text-slate-700">{cat.title}</span>
      </div>
      <ul className="space-y-0.5">
        {resolve(group, cat).map((s) => (
          <li key={s.slug}>
            <Link
              to={group.anchor(s.slug)}
              onClick={onNavigate}
              data-testid={`nav-${group.id}-link-${s.slug}`}
              className="group flex items-center gap-3 -mx-1.5 px-1.5 py-1.5 rounded-sm hover:bg-paper-dark/40 transition-colors"
            >
              <IconTile icon={group.iconFor(s)} size="sm" />
              <span className="min-w-0">
                <span className="block text-[13px] font-semibold leading-tight text-slate-900 group-hover:text-topo transition-colors">
                  {s.name}
                </span>
                <span className="block text-[11px] text-slate-500 truncate">{s.short}</span>
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Survey Techies / CAD Techies dropdown — all services of a single group. */
export default function GroupPanel({ navKey, group, onNavigate }) {
  const GroupIcon = group.icon;
  return (
    <DropdownShell align={group.align} width="w-[860px]" testid={`nav-dropdown-${navKey}`}>
      <div className="px-5 py-3 border-b border-slate-100 bg-paper-dark/20 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <GroupIcon className="h-4 w-4 text-topo" />
          <span className="display text-sm font-extrabold tracking-tight text-slate-900">{group.title}</span>
          <span className="mono text-[9px] tracking-[0.22em] uppercase text-slate-500">· {group.services.length} services</span>
        </div>
        <Link
          to={group.landingTo}
          onClick={onNavigate}
          data-testid={`nav-${navKey}-overview-link`}
          className="mono text-[10px] tracking-[0.22em] uppercase text-topo hover:text-orange-survey transition-colors inline-flex items-center gap-1"
        >
          {group.landingLabel} overview <ArrowRight className="h-3 w-3" />
        </Link>
      </div>
      <div className="grid grid-cols-3 gap-px bg-slate-100">
        {group.categories.map((cat) => (
          <GroupColumn key={cat.title} group={group} cat={cat} onNavigate={onNavigate} />
        ))}
      </div>
      <div className="border-t border-slate-200 bg-paper-dark/30 px-5 py-2.5 flex items-center justify-between">
        {group.footerLinks ? (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
            <span className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">Discipline hubs</span>
            {group.footerLinks.map((l) => (
              <Link
                key={l.key}
                to={l.to}
                onClick={onNavigate}
                data-testid={`nav-${navKey}-hub-${l.key}`}
                className="text-xs font-semibold text-topo hover:text-orange-survey transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        ) : (
          <span className="mono text-[10px] tracking-[0.22em] uppercase text-slate-500">USA · Canada · Australia</span>
        )}
        <Link
          to="/contact#contact-brief"
          onClick={onNavigate}
          data-testid={`nav-${navKey}-cta`}
          className="inline-flex items-center gap-1 rounded-sm bg-orange-survey hover:bg-orange-survey-dark text-white text-xs font-semibold px-3 py-1.5 transition-colors"
        >
          {group.ctaLabel} <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </DropdownShell>
  );
}
